<script setup lang="ts">
import { Form } from '@primevue/forms';
import type { FormSubmitEvent } from '@primevue/forms/form';
import { zodResolver } from '@primevue/forms/resolvers/zod';
import { CircleCheck, CircleX, LoaderCircle, Send } from 'lucide-vue-next';
import { FloatLabel, InputText, Password } from 'primevue';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { z } from 'zod';
import { passwordReset as passwordResetRequest } from '@api/password-reset';
import passwordValidation from '@library/password-validation';

const { t } = useI18n();

const props = defineProps<{
    token?: string;
}>();

const requestError = ref(false);
const requestLoading = ref(false);
const requestSuccess = ref(false);

const onFormSubmit = async (event: FormSubmitEvent) => {
    if (!props.token) {
        requestError.value = true;
        return;
    }

    if (!event.valid) return;

    requestError.value = false;
    requestLoading.value = true;
    requestSuccess.value = false;

    const result = await passwordResetRequest(event.values.password, props.token);

    requestLoading.value = false;
    requestError.value = !result;
    requestSuccess.value = result;
};

const resolver = zodResolver(
    z
        .object({
            confirmPassword: passwordValidation(t),
            password: passwordValidation(t)
        })
        .refine((data) => data.password === data.confirmPassword, {
            message: t('error.passwordsDoNotMatch'),
            path: ['confirmPassword']
        })
);
</script>

<template>
    <Form
        v-if="!requestSuccess && !requestError"
        v-slot="$form"
        class="flex flex-col gap-4"
        :resolver="resolver"
        @submit="onFormSubmit"
        :validateOnValueUpdate="false"
        :validateOnBlur="true">
        <div class="flex flex-col gap-1">
            <!-- Create and hide username field to fix browser warning -->
            <!-- https://www.chromium.org/developers/design-documents/create-amazing-password-forms/ -->
            <InputText
                autocomplete="username"
                class="hidden"
                :disabled="requestLoading"
                name="email"
                type="text"
                fluid
                readonly />
            <FloatLabel variant="on">
                <Password
                    inputId="password"
                    :disabled="requestLoading"
                    name="password"
                    :pt="{
                        pcInputText: { root: { autocomplete: 'new-password' } }
                    }"
                    :promptLabel="$t('chooseAPassword')"
                    :weakLabel="$t('tooWeak')"
                    :mediumLabel="$t('averageComplexity')"
                    :strongLabel="$t('complexPassword')"
                    fluid
                    toggleMask />
                <label for="password">{{ $t('newPassword') }}</label>
            </FloatLabel>
            <Message v-if="$form.password?.invalid" severity="error" size="small" variant="simple">{{
                $form.password.error.message
            }}</Message>
        </div>
        <div class="flex flex-col gap-1">
            <FloatLabel variant="on">
                <Password
                    :feedback="false"
                    :disabled="requestLoading"
                    inputId="confirmPassword"
                    name="confirmPassword"
                    :pt="{
                        pcInputText: { root: { autocomplete: 'new-password' } }
                    }"
                    fluid
                    toggleMask />
                <label for="confirmPassword">{{ $t('confirmNewPassword') }}</label>
            </FloatLabel>
            <Message v-if="$form.confirmPassword?.invalid" severity="error" size="small" variant="simple">{{
                $form.confirmPassword.error.message
            }}</Message>
        </div>
        <div class="flex flex-col-reverse md:flex-row gap-4 justify-between">
            <Button
                :as="requestLoading ? 'button' : 'a'"
                :disabled="requestLoading"
                class="w-full md:w-auto"
                href="#login"
                severity="secondary"
                size="small"
                :label="$t('backToLogin')"
                variant="text" />
            <Button :disabled="requestLoading" type="submit" :label="$t('submit')">
                <template #icon>
                    <LoaderCircle v-if="requestLoading" class="animate-spin" />
                    <Send v-else />
                </template>
            </Button>
        </div>
    </Form>
    <div v-else-if="requestSuccess" class="flex flex-col items-center justify-center flex-1 h-full p-4">
        <CircleCheck :size="48" class="text-green-500 dark:text-green-400 mb-4" />
        <p class="text-center">
            {{ t('passwordResetSuccessful') }}
        </p>
        <Button
            as="a"
            class="w-full md:w-auto mt-8"
            href="#login"
            severity="secondary"
            size="small"
            :label="$t('backToLogin')"
            variant="text" />
    </div>
    <div v-else class="flex flex-col items-center justify-center flex-1 h-full p-4">
        <CircleX :size="48" class="text-red-500 dark:text-red-400 mb-4" />
        <p class="text-center">
            {{ t('passwordResetError') }}
            <br />
            <br />
            {{ $t('error.pleaseTryAgain') }}
        </p>
        <Button
            @click="requestError = false"
            class="w-full md:w-auto mt-8"
            severity="secondary"
            size="small"
            :label="$t('tryAgain')"
            variant="text" />
    </div>
</template>
